import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SequenceUploadCard } from '@/components/admin/SequenceUploadCard';
import { useMigrationProgress } from '@/hooks/useMigrationProgress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, Search, ArrowLeft, RefreshCw, FileUp } from 'lucide-react';

interface SequenceCSVRow {
  id: string;
  title: string;
  storage_path: string;
  download_count: number;
  user_id: string;
  created_at: string;
}

export default function MigrationUploader() {
  const navigate = useNavigate();
  const [sequences, setSequences] = useState<SequenceCSVRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'complete' | 'skipped'>('all');
  const [isParsingCSV, setIsParsingCSV] = useState(false);

  const {
    progressRecords,
    stats,
    isLoading,
    initializeProgress,
    resetProgress,
    isInitializing,
    isResetting,
  } = useMigrationProgress();

  const parseCSV = useCallback((text: string): SequenceCSVRow[] => {
    const lines = text.trim().split('\n');
    if (lines.length === 0) return [];

    // Parse header
    const headers = lines[0].split(',').map(h => h.trim());
    const idIndex = headers.findIndex(h => h === 'id');
    const titleIndex = headers.findIndex(h => h === 'title');
    const storagePathIndex = headers.findIndex(h => h === 'storage_path');
    const downloadCountIndex = headers.findIndex(h => h === 'download_count');
    const userIdIndex = headers.findIndex(h => h === 'user_id');
    const createdAtIndex = headers.findIndex(h => h === 'created_at');

    if (idIndex === -1 || titleIndex === -1 || storagePathIndex === -1) {
      throw new Error('CSV must contain id, title, and storage_path columns');
    }

    // Parse rows
    const rows: SequenceCSVRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, '')); // Remove quotes

      rows.push({
        id: values[idIndex],
        title: values[titleIndex],
        storage_path: values[storagePathIndex],
        download_count: downloadCountIndex !== -1 ? parseInt(values[downloadCountIndex]) || 0 : 0,
        user_id: userIdIndex !== -1 ? values[userIdIndex] : '',
        created_at: createdAtIndex !== -1 ? values[createdAtIndex] : new Date().toISOString(),
      });
    }

    return rows;
  }, []);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsParsingCSV(true);
    try {
      const text = await file.text();
      const parsedSequences = parseCSV(text);

      if (parsedSequences.length === 0) {
        alert('No sequences found in CSV file');
        return;
      }

      setSequences(parsedSequences);

      // Initialize progress tracking for all sequences
      const sequenceIds = parsedSequences.map(s => s.id);
      initializeProgress(sequenceIds);

    } catch (error) {
      console.error('Error parsing CSV:', error);
      alert(`Failed to parse CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsParsingCSV(false);
      // Reset file input
      e.target.value = '';
    }
  }, [parseCSV, initializeProgress]);

  const handleReset = useCallback(() => {
    resetProgress();
    setSequences([]);
    setSearchQuery('');
    setStatusFilter('all');
  }, [resetProgress]);

  // Filter and search sequences
  const filteredSequences = useMemo(() => {
    let filtered = sequences;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(seq => {
        const progress = progressRecords.find(p => p.sequence_id === seq.id);
        return progress?.status === statusFilter;
      });
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(seq =>
        seq.title.toLowerCase().includes(query) ||
        seq.id.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [sequences, searchQuery, statusFilter, progressRecords]);

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">OneDrive Migration Uploader</h1>
            <p className="text-muted-foreground mt-1">
              Upload sequence files and track migration progress
            </p>
          </div>
        </div>
        {sequences.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={isResetting}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset All Progress
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset All Progress?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will clear all migration progress and uploaded sequences. This action cannot be undone.
                  You will need to re-upload the CSV file.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset}>Reset Everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* CSV Upload / Progress Stats */}
      {sequences.length === 0 ? (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Upload Sequences CSV</CardTitle>
            <CardDescription>
              Upload the CSV file exported from Supabase with OneDrive sequences (532 sequences)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label htmlFor="csv-upload">
              <div className="border-2 border-dashed rounded-lg p-12 text-center hover:border-gray-400 transition-colors cursor-pointer">
                <FileUp className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  Click to upload CSV file
                </p>
                <p className="text-sm text-gray-500">
                  Required columns: id, title, storage_path
                </p>
                <Button variant="outline" className="mt-4" disabled={isParsingCSV} asChild>
                  <span>
                    {isParsingCSV ? 'Parsing...' : 'Browse Files'}
                  </span>
                </Button>
              </div>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileUpload}
                disabled={isParsingCSV}
              />
            </label>

            <Alert className="mt-4">
              <AlertDescription>
                <strong>CSV Format:</strong> The CSV file should have columns: id, title, storage_path, download_count, user_id, created_at.
                Export from Supabase SQL Editor or use the provided query from migration/onedrive-sequences.csv.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Progress Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Sequences</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{sequences.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{stats.complete}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Skipped</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-600">{stats.skipped}</div>
              </CardContent>
            </Card>
          </div>

          {/* Overall Progress Bar */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Overall Progress</CardTitle>
              <CardDescription>
                {stats.complete} of {sequences.length} sequences migrated ({stats.percentComplete}%)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={stats.percentComplete} className="h-3" />
            </CardContent>
          </Card>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by title or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending Only</SelectItem>
                <SelectItem value="complete">Completed Only</SelectItem>
                <SelectItem value="skipped">Skipped Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sequence Cards */}
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              Loading progress...
            </div>
          ) : filteredSequences.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No sequences match your filters
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSequences.map((sequence) => {
                const progress = progressRecords.find(p => p.sequence_id === sequence.id);
                return (
                  <SequenceUploadCard
                    key={sequence.id}
                    sequence={sequence}
                    status={progress?.status}
                    onUploadComplete={() => {
                      // Progress will auto-refresh via React Query
                    }}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
